"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Button } from "@workspace/ui/components";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useInView } from "motion/react";

type VideoPlayerProps = {
  isActive: boolean;
};

export const VideoPlayer = ({ isActive }: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(videoRef, { margin: "-100px 0px -100px 0px" });
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleAudio = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  };

  const stop = () => {
    videoRef.current?.pause();
  };

  const play = () => {
    videoRef.current?.play();
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeChange = () => setMuted(!!video.muted);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("volumechange", handleVolumeChange);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("volumechange", handleVolumeChange);
    };
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    if (isActive && isInView) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }, [isActive, isInView]);

  return (
    <div className="relative w-full h-full">
      {isPlaying && (
        <div className="absolute bottom-0 left-0 w-full h-fit z-20 p-5 gap-5 flex items-center">
          <Button
            onClick={stop}
            variant="d-primary"
            className="border-none size-[50px]"
          >
            <Pause size={24} fill="black" />
          </Button>
          <Button
            onClick={toggleAudio}
            variant="d-primary"
            className="border-none size-[50px]"
          >
            {muted ? (
              <VolumeX size={24} fill="black" />
            ) : (
              <Volume2 size={24} fill="black" />
            )}
          </Button>
        </div>
      )}
      <video
        ref={videoRef}
        src="/trailer.webm"
        className="w-full h-full absolute top-0 left-0 object-cover"
        controls={false}
        loop
        muted
        playsInline
      />

      {!isPlaying && (
        <>
          <Image
            src="/preview.jpg"
            alt="preview"
            width={1100}
            height={780}
            className="object-cover absolute top-0 left-0 w-full h-full"
          />
          <div className="absolute flex items-center justify-center w-full h-full bg-[rgba(37,25,3,.56)] top-0 left-0">
            <Button
              onClick={play}
              variant="d-primary"
              className="border-none size-[50px]"
            >
              <Play size={24} fill="black" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
